import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, SparklesIcon, BotIcon, UserIcon, MapPinIcon, WalletIcon, ClockIcon, CarIcon, CheckCircle2Icon, TrendingUpIcon, LightbulbIcon, ArrowDownIcon, TargetIcon, PhoneIcon, MailIcon, MessageCircleIcon, FileTextIcon, ListIcon, StickyNoteIcon, ShieldCheckIcon, ShieldAlertIcon, PlusIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
export const PIPELINE_STAGES = ['Discovered', 'Qualified', 'Interested', 'Negotiating', 'Ready To Buy', 'Won', 'Lost'] as const;
export type LeadStatus = (typeof PIPELINE_STAGES)[number];
export const OWNERS = ['Unassigned', 'Fadi', 'Sara', 'Sales Team'] as const;
export interface Lead {
  name: string;
  intentScore: number;
  lookingFor: string;
  budget: string;
  timeline: string;
  location: string;
  matchedItem: string;
  status: LeadStatus;
  summary: string;
  // Contact / CRM (only present once customer has engaged)
  phone?: string;
  email?: string;
  preferredContact?: string;
  preferredLanguage?: string;
  source?: string;
  lastActive?: string;
  receivedDate?: string;
  owner?: string;
  nextAction?: string;
  nextActionDue?: string;
  consent?: 'shared' | 'requested' | 'not_shared';
}
interface LeadDetailModalProps {
  lead: Lead;
  onClose: () => void;
  onStatusChange?: (status: LeadStatus) => void;
  onOwnerChange?: (owner: string) => void;
}
type DetailTab = 'Overview' | 'Conversation Timeline' | 'Customer Details' | 'Matched Listings' | 'AI Recommendations' | 'Activity Log' | 'Notes';
function intentBucket(score: number) {
  if (score >= 90) return {
    label: 'Ready To Buy',
    color: 'text-green-700',
    bg: 'bg-green-100',
    bar: 'bg-green-500'
  };
  if (score >= 75) return {
    label: 'High Intent',
    color: 'text-primary-indigo',
    bg: 'bg-indigo-100',
    bar: 'bg-primary-indigo'
  };
  if (score >= 50) return {
    label: 'Interested',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    bar: 'bg-amber-500'
  };
  return {
    label: 'Early Stage',
    color: 'text-slate-500',
    bg: 'bg-slate-100',
    bar: 'bg-slate-400'
  };
}
export function LeadDetailModal({
  lead,
  onClose,
  onStatusChange,
  onOwnerChange
}: LeadDetailModalProps) {
  const bucket = intentBucket(lead.intentScore);
  const [tab, setTab] = useState<DetailTab>('Overview');
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [owner, setOwner] = useState<string>(lead.owner ?? 'Unassigned');
  const [notes, setNotes] = useState('Customer prefers white GCC vehicles only. Do not suggest imported vehicles.');
  const [activity, setActivity] = useState<{
    text: string;
    time: string;
  }[]>([{
    text: 'Lead created from Talab AI conversation',
    time: '2 days ago'
  }, {
    text: 'Customer asked about delivery options',
    time: '2 days ago'
  }, {
    text: 'Customer requested seller contact',
    time: '1 day ago'
  }, {
    text: `Owner set to ${lead.owner ?? 'Sara'}`,
    time: '1 day ago'
  }]);
  const hasContact = lead.consent === 'shared';
  const changeStatus = (next: LeadStatus) => {
    if (next === status) return;
    setActivity(a => [{
      text: `Status changed from ${status} to ${next} by Fadi`,
      time: 'Just now'
    }, ...a]);
    setStatus(next);
    onStatusChange?.(next);
  };
  const changeOwner = (next: string) => {
    if (next === owner) return;
    setActivity(a => [{
      text: `Owner changed from ${owner} to ${next} by Fadi`,
      time: 'Just now'
    }, ...a]);
    setOwner(next);
    onOwnerChange?.(next);
  };
  const tabs: {
    id: DetailTab;
    icon: React.ReactNode;
  }[] = [{
    id: 'Overview',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-2504" data-mp="12zw0y3zksvc7" />
  }, {
    id: 'Conversation Timeline',
    icon: <MessageCircleIcon className="w-4 h-4" data-id="element-2505" data-mp="17yytioydd93a" />
  }, {
    id: 'Customer Details',
    icon: <UserIcon className="w-4 h-4" data-id="element-2506" data-mp="2yg2q5j854dz8" />
  }, {
    id: 'Matched Listings',
    icon: <CarIcon className="w-4 h-4" data-id="element-2507" data-mp="2ten4pq21w5gb" />
  }, {
    id: 'AI Recommendations',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-2508" data-mp="37e4m3kd0ogfn" />
  }, {
    id: 'Activity Log',
    icon: <ListIcon className="w-4 h-4" data-id="element-2509" data-mp="33e463va9rx6h" />
  }, {
    id: 'Notes',
    icon: <StickyNoteIcon className="w-4 h-4" data-id="element-2510" data-mp="2fmyu5r7g9p1g" />
  }];
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2511" data-mp="2fl46w68phku7">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-2512" data-mp="2amas8ahonnsg" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} className="relative w-full max-w-6xl bg-[#F8FAFC] rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[88vh]" data-id="element-2513" data-mp="2ohn0lkorhcru">
        {/* Header with status + owner controls */}
        <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-2514" data-mp="1mm8rnm506921">
          <div className="flex items-center gap-3" data-id="element-2515" data-mp="1cumj5xk1uo2z">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center text-white font-bold" data-id="element-2516" data-mp="1cv5s4orq224v">
              {lead.name.charAt(0)}
            </div>
            <div data-id="element-2517" data-mp="25hh1ooxb3ddt">
              <h2 className="text-lg font-display font-bold text-dark-navy" data-id="element-2518" data-mp="1qmauhymh58iw">
                {lead.name}
              </h2>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${bucket.bg} ${bucket.color}`} data-id="element-2519" data-mp="1vkug1xx5oiaq">
                {bucket.label} · {lead.intentScore}%
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-2520" data-mp="k7iczp9803pm">
            <Dropdown label="Status" value={status} options={PIPELINE_STAGES as unknown as string[]} onChange={v => changeStatus(v as LeadStatus)} data-id="element-2521" data-mp="2mtdzaoiq87pz" />
            <Dropdown label="Owner" value={owner} options={OWNERS as unknown as string[]} onChange={changeOwner} data-id="element-2522" data-mp="2d2171vt0nfjf" />
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-2523" data-mp="23554e6pstnms">
              <XIcon className="w-5 h-5" data-id="element-2524" data-mp="1u3yjzmk13so9" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden" data-id="element-2525" data-mp="1p53vc3zdr9w8">
          {/* Tab rail */}
          <div className="w-52 flex-shrink-0 border-r border-slate-100 bg-white p-3 overflow-y-auto hidden md:block" data-id="element-2526" data-mp="1p7gjiyvqeghq">
            <div className="space-y-1" data-id="element-2527" data-mp="3jjeh23kohnxs">
              {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-indigo-50 text-primary-indigo' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-2528" data-mp="3jh25c85ik9t6">
                  <span className={tab === t.id ? 'text-primary-indigo' : 'text-slate-400'} data-id="element-2529" data-mp="9mpfe8erd791">
                    {t.icon}
                  </span>
                  {t.id}
                </button>)}
            </div>
          </div>

          {/* Mobile tab select */}
          <div className="md:hidden absolute top-[88px] left-0 right-0 px-4 z-10" data-id="element-2530" data-mp="3i42dqiae91tz">
            <select value={tab} onChange={e => setTab(e.target.value as DetailTab)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm shadow-sm" data-id="element-2531" data-mp="3n2dlmnh4p29k">
              {tabs.map(t => <option key={t.id} value={t.id} data-id="element-2532" data-mp="1nfxgcjyhxinh">
                  {t.id}
                </option>)}
            </select>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 md:pt-6 pt-16" data-id="element-2533" data-mp="1x577nxlimxaj">
            {tab === 'Overview' && <div className="space-y-6 max-w-3xl" data-id="element-2534" data-mp="1sdp6b8fkyup1">
                <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-soft" data-id="element-2535" data-mp="1sbvz9s1228gi">
                  <div className="flex items-center justify-between mb-1" data-id="element-2536" data-mp="267gh0k51skmc">
                    <span className="text-sm text-slate-500" data-id="element-2537" data-mp="2230fev2gvzcx">Intent Score</span>
                    <span className="font-bold text-dark-navy" data-id="element-2538" data-mp="2g3t6t6xh2ebt">
                      {lead.intentScore}% · {bucket.label}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2539" data-mp="lpa699whqkwf">
                    <div className={`h-full ${bucket.bar}`} style={{
                  width: `${lead.intentScore}%`
                }} data-id="element-2540" data-mp="gqff9koddq18" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-2541" data-mp="2xcccytbrojvb">
                  <Info label="Looking For" value={lead.lookingFor} data-id="element-2542" data-mp="2ioivf334f7a3" />
                  <Info label="Budget" value={lead.budget} data-id="element-2543" data-mp="2im514wg9lz3l" />
                  <Info label="Timeline" value={lead.timeline} data-id="element-2544" data-mp="2nlrdl3o9rbbc" />
                  <Info label="Location" value={lead.location} data-id="element-2545" data-mp="29mrs24w1zcje" />
                  <Info label="Matched Listing" value={lead.matchedItem} data-id="element-2546" data-mp="29pduxr8013va" />
                  <Info label="Status" value={status} data-id="element-2547" data-mp="2enyxvyemy1nf" />
                </div>
                <NextActionCard lead={lead} data-id="element-2548" data-mp="1zq6m9hc7lxei" />
                <div data-id="element-2549" data-mp="1zr7ule1uc5sj">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2550" data-mp="24nybu4pdt7fj">
                    Why Talab Matched This Lead
                  </h3>
                  <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-soft grid grid-cols-2 gap-2.5" data-id="element-2551" data-mp="3u11swe3u32en">
                    {['Budget Match', 'Location Match', 'Category Match', 'Timeline Match', 'Preference Match'].map(m => <div key={m} className="flex items-center gap-2 text-sm text-dark-navy" data-id="element-2552" data-mp="j2ch6vbgy5hz">
                        <CheckCircle2Icon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-2553" data-mp="1bp6zblktcssa" />{' '}
                        {m}
                      </div>)}
                  </div>
                </div>
              </div>}

            {tab === 'Conversation Timeline' && <div className="max-w-2xl" data-id="element-2554" data-mp="16pc79epaxx5n">
                <p className="text-sm text-slate-500 mb-6" data-id="element-2555" data-mp="16sghottd1p4r">
                  How Talab qualified this lead.
                </p>
                <div className="space-y-1" data-id="element-2556" data-mp="3rgcpvrjgaqpg">
                  <TimelineBubble role="customer" text="Find me a Prado." data-id="element-2557" data-mp="3ri5wx7xz7cxz" />
                  <TimelineArrow data-id="element-2558" data-mp="29i6kqmpyk8kh" />
                  <TimelineBubble role="talab" text="What's your budget?" data-id="element-2559" data-mp="29h4xzkzkxxmx" />
                  <TimelineArrow data-id="element-2560" data-mp="24j3eiyy8rwax" />
                  <TimelineBubble role="customer" text="AED 90,000" data-id="element-2561" data-mp="24lzc2m66giyu" />
                  <TimelineArrow data-id="element-2562" data-mp="2iicxg6du14il" />
                  <TimelineBubble role="talab" text="Preferred mileage?" data-id="element-2563" data-mp="2ihbk1v9cg5xp" />
                  <TimelineArrow data-id="element-2564" data-mp="2eikm5lx20k9j" />
                  <TimelineBubble role="customer" text="Under 100,000 km" data-id="element-2565" data-mp="1qshuh6x1xgbg" />
                  <TimelineArrow data-id="element-2566" data-mp="1qqdsiuipqrdt" />
                  <TimelineBubble role="talab" text="Recommended 3 vehicles" data-id="element-2567" data-mp="1ltn9pzfixhop" />
                  <TimelineArrow data-id="element-2568" data-mp="1lrkft4b934ep" />
                  <TimelineBubble role="customer" text="Can I see this vehicle?" data-id="element-2569" data-mp="1zl1uobszrsye" />
                  <TimelineArrow data-id="element-2570" data-mp="1ussbdemdtr53" />
                  <div className="flex items-center gap-2 mt-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl" data-id="element-2571" data-mp="17491iunop6aw">
                    <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-2572" data-mp="133p55taxq6pt" />
                    <span className="font-bold text-green-700 text-sm" data-id="element-2573" data-mp="327xawhrggn4v">
                      Lead Created
                    </span>
                  </div>
                </div>
              </div>}

            {tab === 'Customer Details' && <div className="max-w-2xl" data-id="element-2574" data-mp="3by8rp4w8mqt6">
                <ConsentBanner consent={lead.consent} data-id="element-2575" data-mp="3gwa97le0mi2j" />
                {hasContact ? <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-soft grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-id="element-2576" data-mp="2j8t6xnertjk2">
                    <Info label="Name" value={lead.name} data-id="element-2577" data-mp="2o8xq5l72csdz" />
                    <Info label="Phone" value={lead.phone ?? '—'} data-id="element-2578" data-mp="2t6z8g3wo0noe" />
                    <Info label="Email" value={lead.email ?? '—'} data-id="element-2579" data-mp="2t5o2bxfqz3u4" />
                    <Info label="Preferred Language" value={lead.preferredLanguage ?? 'English'} data-id="element-2580" data-mp="2y73lfjyd709v" />
                    <Info label="Preferred Contact" value={lead.preferredContact ?? '—'} data-id="element-2581" data-mp="dfl1hw2p52ba" />
                    <Info label="City" value={lead.location} data-id="element-2582" data-mp="1xxcfl8jn3ygd" />
                    <Info label="Budget" value={lead.budget} data-id="element-2583" data-mp="1syiy7518fnax" />
                    <Info label="Timeline" value={lead.timeline} data-id="element-2584" data-mp="1t156j6vy66u7" />
                  </div> : <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center shadow-soft mt-4" data-id="element-2585" data-mp="1j8ze5w1s9neh">
                    <ShieldAlertIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-2586" data-mp="1eaedjvimd1pi" />
                    <h4 className="font-bold text-dark-navy mb-1" data-id="element-2587" data-mp="1a8jdsvgc6wdu">
                      Contact not shared yet
                    </h4>
                    <p className="text-sm text-slate-500" data-id="element-2588" data-mp="159zqaqm3hcjd">
                      Customer has not requested seller contact.
                    </p>
                  </div>}
              </div>}

            {tab === 'Matched Listings' && <div className="max-w-2xl space-y-3" data-id="element-2589" data-mp="2unmq2iy0tzxk">
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-soft flex items-center gap-3" data-id="element-2590" data-mp="2uoxrwd7woxm1">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-2591" data-mp="1s8vxc2fj1o0k">
                    <img src="https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=120" alt={lead.matchedItem} className="w-full h-full object-cover" data-id="element-2592" data-mp="1w9zb8je0l9z5" />
                  </div>
                  <div className="flex-1" data-id="element-2593" data-mp="18kwl0n5be8t1">
                    <div className="font-bold text-dark-navy text-sm" data-id="element-2594" data-mp="1ddpn139ef31f">
                      {lead.matchedItem}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-2595" data-mp="znvzqpdjkqtt">
                      98% match · {lead.budget}
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full" data-id="element-2596" data-mp="pnlmx5vf5lf6">
                    Best match
                  </span>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-soft flex items-center gap-3" data-id="element-2597" data-mp="b0wrpg879uel">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-2598" data-mp="70mjdwwxqrwk">
                    <img src="https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=120" alt="Alt" className="w-full h-full object-cover" data-id="element-2599" data-mp="l0drjh0rte2n" />
                  </div>
                  <div className="flex-1" data-id="element-2600" data-mp="pyyuvpbbkdvb">
                    <div className="font-bold text-dark-navy text-sm" data-id="element-2601" data-mp="py6xqa22q82x">
                      Nissan Patrol 2020
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-2602" data-mp="yr0r1becv7zs">81% match</div>
                  </div>
                </div>
              </div>}

            {tab === 'AI Recommendations' && <div className="max-w-2xl space-y-6" data-id="element-2603" data-mp="18p6qznglq420">
                <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-5 text-white shadow-medium" data-id="element-2604" data-mp="18qhwpstlxlvr">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2605" data-mp="1fqaa11kpxesz">
                    <SparklesIcon className="w-4 h-4" data-id="element-2606" data-mp="1krzlgiburuhl" />
                    <span className="font-bold text-sm" data-id="element-2607" data-mp="1pqkosqmeiua9">AI Buyer Analysis</span>
                  </div>
                  <ul className="space-y-2 text-sm text-indigo-50" data-id="element-2608" data-mp="1uhj7txyry6h7">
                    <li className="flex gap-2" data-id="element-2609" data-mp="1uiub7wob5c7s">
                      <span className="text-electric-cyan" data-id="element-2610" data-mp="1zf2kwujqkd7k">•</span> High
                      probability buyer.
                    </li>
                    <li className="flex gap-2" data-id="element-2611" data-mp="23fwbxwsh684o">
                      <span className="text-electric-cyan" data-id="element-2612" data-mp="28gafp2n1mvww">•</span> Actively
                      comparing GCC vehicles.
                    </li>
                    <li className="flex gap-2" data-id="element-2613" data-mp="2df3wp51jh51t">
                      <span className="text-electric-cyan" data-id="element-2614" data-mp="e3avsxbmrfjt">•</span> Likely
                      purchase timeline: 7 days.
                    </li>
                    <li className="flex gap-2" data-id="element-2615" data-mp="j24d70u1fqp9">
                      <span className="text-electric-cyan" data-id="element-2616" data-mp="biv2n49splym">•</span> Budget
                      flexibility: ~AED 5,000.
                    </li>
                  </ul>
                </div>
                <div className="space-y-3" data-id="element-2617" data-mp="1sa5zmdomric">
                  {['Offer financing options', 'Do not suggest imported vehicles', 'Highlight service history', 'Offer delivery'].map(rec => <RecommendationRow key={rec} text={rec} data-id="element-2618" data-mp="pi4g3w6ma1yw" />)}
                </div>
                <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-xl px-4 py-3" data-id="element-2619" data-mp="kirkalxrpkk6">
                  <span className="text-sm font-medium text-green-800" data-id="element-2620" data-mp="fkq0tzwfjj86">
                    Probability of conversion
                  </span>
                  <span className="text-lg font-bold text-green-700" data-id="element-2621" data-mp="3575u6vtahsk6">87%</span>
                </div>
              </div>}

            {tab === 'Activity Log' && <div className="max-w-2xl" data-id="element-2622" data-mp="3pxjg8uv4xern">
                <div className="relative border-l-2 border-slate-100 ml-2 space-y-6" data-id="element-2623" data-mp="3uxe9h52dugfv">
                  {activity.map((a, i) => <div key={i} className="relative pl-6" data-id="element-2624" data-mp="3gyeny6a62hnx" data-mpi={i}>
                      <div className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-primary-indigo border-2 border-white" data-id="element-2625" data-mp="3gzzdfhhmjaex" />
                      <div className="text-sm font-medium text-dark-navy" data-id="element-2626" data-mp="cj512l179ro9">
                        {a.text}
                      </div>
                      <div className="text-xs text-slate-400" data-id="element-2627" data-mp="hhzvylcfz6oq">{a.time}</div>
                    </div>)}
                </div>
              </div>}

            {tab === 'Notes' && <div className="max-w-2xl" data-id="element-2628" data-mp="7qvcp9fqgzo3">
                <h3 className="text-sm font-bold text-dark-navy mb-2" data-id="element-2629" data-mp="vhr445ga1sut">
                  Internal Team Notes
                </h3>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={6} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 text-sm resize-none shadow-soft" placeholder="Add a note for your team…" data-id="element-2630" data-mp="109slu6w7sbs8" />
                <button className="mt-3 px-5 py-2.5 bg-primary-indigo text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors" data-id="element-2631" data-mp="2pr5z4ow5inn4">
                  Save Note
                </button>
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
}
function Dropdown({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return <div className="relative" data-id="element-2632" data-mp="3ndv7rhlczyhy">
      <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} className="appearance-none pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-dark-navy focus:ring-2 focus:ring-primary-indigo/20 cursor-pointer" data-id="element-2633" data-mp="3nctx3e899e0r">
        {options.map(o => <option key={o} value={o} data-id="element-2634" data-mp="g0m4x5mj58y2">
            {label}: {o}
          </option>)}
      </select>
      <ChevronDownIcon className="w-4 h-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-2635" data-mp="tvxwx59awlba" />
    </div>;
}
function Info({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="bg-white rounded-xl border border-slate-100 px-4 py-3 shadow-soft" data-id="element-2636" data-mp="yxezd57foftz">
      <div className="text-xs text-slate-400 mb-0.5" data-id="element-2637" data-mp="yvkic1za97v6">{label}</div>
      <div className="text-sm font-bold text-dark-navy" data-id="element-2638" data-mp="1coaayp9bhb19">{value}</div>
    </div>;
}
function NextActionCard({
  lead
}: {
  lead: Lead;
}) {
  return <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-soft" data-id="element-2639" data-mp="1mmpzckgym672">
      <div className="flex items-center justify-between mb-3" data-id="element-2640" data-mp="1mkn49m0y9mvh">
        <h3 className="text-sm font-bold text-dark-navy" data-id="element-2641" data-mp="1hpg4gpasx2bu">Next Action</h3>
        <span className="text-xs text-amber-600 font-medium flex items-center gap-1" data-id="element-2642" data-mp="219evccc0618p">
          <ClockIcon className="w-3.5 h-3.5" data-id="element-2643" data-mp="269b2gkfzy8sh" /> Due{' '}
          {lead.nextActionDue ?? 'Tomorrow'}
        </span>
      </div>
      <select defaultValue={lead.nextAction ?? 'Call customer'} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2644" data-mp="1rapdgjk3wgsg">
        {['Call customer', 'Send WhatsApp', 'Follow up tomorrow', 'Send quotation', 'Book viewing', 'Offer delivery', 'Close as lost'].map(a => <option key={a} data-id="element-2645" data-mp="1dk3vcx2h8avm">{a}</option>)}
      </select>
      <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-2646" data-mp="1dkdkyjjm0g0s">
        <input type="checkbox" defaultChecked className="rounded text-primary-indigo focus:ring-primary-indigo" data-id="element-2647" data-mp="zldy9hfnqb79" />{' '}
        Set reminder
      </label>
    </div>;
}
function RecommendationRow({
  text
}: {
  text: string;
}) {
  const [state, setState] = useState<'open' | 'done' | 'ignored'>('open');
  return <div className={`flex items-center justify-between gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3 shadow-soft ${state !== 'open' ? 'opacity-60' : ''}`} data-id="element-2648" data-mp="31g1vflxm5z9f">
      <span className="flex items-center gap-2 text-sm text-slate-700" data-id="element-2649" data-mp="2rlicaefsypry">
        <SparklesIcon className="w-4 h-4 text-ai-purple flex-shrink-0" data-id="element-2650" data-mp="3p64mpztpf16z" />
        {text}
        {state === 'done' && ' · Done'}
        {state === 'ignored' && ' · Ignored'}
      </span>
      {state === 'open' && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-2651" data-mp="3f7ows06exxz2">
          <button className="px-2.5 py-1 bg-primary-indigo text-white text-xs font-medium rounded-md hover:bg-indigo-700" data-id="element-2652" data-mp="3b8fz4k895fy8">
            Create Message
          </button>
          <button onClick={() => setState('done')} className="p-1.5 text-green-600 hover:bg-green-50 rounded-md" title="Mark Done" data-id="element-2653" data-mp="1zsj208q9fppx">
            <CheckIcon className="w-4 h-4" data-id="element-2654" data-mp="f8dxj0pl05jm" />
          </button>
          <button onClick={() => setState('ignored')} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-md" title="Ignore" data-id="element-2655" data-mp="jcve6r0na2q6">
            <XIcon className="w-4 h-4" data-id="element-2656" data-mp="t255i4nnzhd8" />
          </button>
        </div>}
    </div>;
}
function ConsentBanner({
  consent
}: {
  consent?: Lead['consent'];
}) {
  if (consent === 'shared') return <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl text-sm text-green-800" data-id="element-2657" data-mp="12z9sg6pc3gv3">
        <ShieldCheckIcon className="w-4 h-4" data-id="element-2658" data-mp="1726a9v71v8xz" /> Contact shared by customer
      </div>;
  if (consent === 'requested') return <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800" data-id="element-2659" data-mp="38t16up92zc5r">
        <ShieldAlertIcon className="w-4 h-4" data-id="element-2660" data-mp="3drb2t22mqgsf" /> Contact requested
      </div>;
  return <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600" data-id="element-2661" data-mp="2ihehb3ggnq4o">
      <ShieldAlertIcon className="w-4 h-4" data-id="element-2662" data-mp="2di1n1xn9fgs2" /> Contact not shared yet
    </div>;
}
function TimelineBubble({
  role,
  text
}: {
  role: 'customer' | 'talab';
  text: string;
}) {
  const isTalab = role === 'talab';
  return <div className={`flex gap-2 ${isTalab ? '' : 'flex-row-reverse'}`} data-id="element-2663" data-mp="2nexxi8wpycz9">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isTalab ? 'bg-gradient-to-br from-primary-indigo to-ai-purple text-white' : 'bg-slate-200 text-slate-600'}`} data-id="element-2664" data-mp="1zs7zdinhr2jx">
        {isTalab ? <BotIcon className="w-3.5 h-3.5" data-id="element-2665" data-mp="29j2hcatpffzk" /> : <UserIcon className="w-3.5 h-3.5" data-id="element-2666" data-mp="24prdmik6vfbv" />}
      </div>
      <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${isTalab ? 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-soft' : 'bg-primary-indigo text-white rounded-tr-sm'}`} data-id="element-2667" data-mp="1h0f23c1e2v6e">
        {text}
      </div>
    </div>;
}
function TimelineArrow() {
  return <div className="flex justify-center py-0.5" data-id="element-2668" data-mp="3sgfollbx5myy">
      <ArrowDownIcon className="w-4 h-4 text-slate-300" data-id="element-2669" data-mp="3sh8ny18jta72" />
    </div>;
}